import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  ACCOUNT_SETUP_ROUTE,
  EMAIL,
  EMAIL_ALREADY_EXISTS,
  EMAIL_LANGUAGE,
  MODE_ALL,
  PHONE_NUM_ALREADY_EXISTS,
  SPACING_4_5,
} from "../../../Constants/CommonConstants";
import {
  DATE_OF_BIRTH,
  FIRST_NAME,
  LAST_NAME,
  NAME_AND_CONTACT_FORM,
  PHONE,
  PHONE_NUMBER,
  PREFERRED_CONTACT,
  VERIFY_ACTION,
} from "../../../Constants/NameAndContactConstants";
import {
  clearAccountCreationApiResponse,
  postVerificationMethod,
  setNameAndContactDetails,
} from "../../../Features/AccountCreationSlice";
import { manageAccountCreationSelector } from "../../../Selectors/AccountCreationSelector";
import { StyledFormInputsBox } from "../../../Styles/Common/CommonStyles";
import AccountActionButtons from "../AccountActionButtons";
import EmailOrPhoneSelectionCards from "./EmailOrPhoneSelectionCards";
import NameAndContactFormInputs from "./NameAndContactFormInputs";
import NameAndContactInfoAlert from "./NameAndContactInfoAlert";
import PreferredVerificationMethod from "./PreferredVerificationMethod";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import {
  SLICE_ACCOUNT_CREATION,
  THUNK_POST_VERIFICATION_METHOD,
} from "../../../Constants/SliceAndThunkConstants";
import { trimData } from "../../../Services/CommonServices";
import { manageReferenceDataSelector } from "../../../Selectors/ReferenceDataSelector";

/**
 * @description  From that collects user basic details for account creation
 * @param {function} setIsAccountAlreadyExists Setter method to update the boolean flag
 * @returns {ReactNode} Render the form inputs and selection card to collect user basic details.
 */
const NameAndContactInfoForm = ({ setIsAccountAlreadyExists }) => {
  const {
    firstName,
    lastName,
    dateOfBirth,
    preferredContact,
    email,
    phone,
    emailLanguage,
  } = useSelector((state) => manageAccountCreationSelector(state));
  const { englishEmailLanguage } = useSelector((state) =>
    manageReferenceDataSelector(state)
  );

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_ACCOUNT_CREATION,
      THUNK_POST_VERIFICATION_METHOD
    )
  );

  const { isFailure, isSuccess, description } = apiResponses;

  const methods = useForm({
    mode: MODE_ALL,
    defaultValues: {
      [FIRST_NAME]: firstName,
      [LAST_NAME]: lastName,
      [DATE_OF_BIRTH]: dateOfBirth,
      [PREFERRED_CONTACT]: preferredContact,
      [EMAIL]: email,
      [PHONE]: phone,
      [EMAIL_LANGUAGE]: emailLanguage || englishEmailLanguage,
    },
  });

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const navigateToAccountHome = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };

  const handleOnSubmit = (data) => {
    const formData = trimData(data);
    dispatch(setNameAndContactDetails(formData));
    const preferredContactAPIName =
      data.preferredContact === PHONE ? PHONE_NUMBER : EMAIL;
    const preferredContact = data.preferredContact === PHONE ? PHONE : EMAIL;

    dispatch(
      postVerificationMethod({
        data: {
          verificationMethod: preferredContact,
          [preferredContactAPIName]: data[preferredContact],
        },
        apiAction: VERIFY_ACTION,
      })
    );
  };

  /**
   * @description Navigating to Account Setup Screen if the response is successful, if unsuccessful
   * and the description is one of EMAIL_ALREADY_EXISTS or PHONE_NUM_ALREADY_EXISTS open
   * the modal.
   */
  useEffect(() => {
    if (isSuccess) {
      navigate(`/${ACCOUNT_CREATION_ROUTE}/${ACCOUNT_SETUP_ROUTE}`);
    }
    if (
      isFailure &&
      (description === EMAIL_ALREADY_EXISTS ||
        description === PHONE_NUM_ALREADY_EXISTS)
    ) {
      setIsAccountAlreadyExists(true);
    }
    dispatch(clearAccountCreationApiResponse(THUNK_POST_VERIFICATION_METHOD));
  }, [
    description,
    isFailure,
    isSuccess,
    navigate,
    setIsAccountAlreadyExists,
    dispatch,
  ]);

  return (
    <FormProvider {...methods}>
      <form
        id={NAME_AND_CONTACT_FORM}
        onSubmit={methods.handleSubmit(handleOnSubmit)}
      >
        <Stack spacing={SPACING_4_5}>
          <NameAndContactFormInputs />
          <PreferredVerificationMethod />
          <StyledFormInputsBox>
            <EmailOrPhoneSelectionCards />
          </StyledFormInputsBox>
          <NameAndContactInfoAlert />
        </Stack>
        <AccountActionButtons cancelClickHandler={navigateToAccountHome} />
      </form>
    </FormProvider>
  );
};

export default NameAndContactInfoForm;

NameAndContactInfoForm.propTypes = {
  setIsAccountAlreadyExists: PropTypes.func.isRequired,
};
